import type { ApiClient } from '../client';
import type {
  ApiResponse,
  BulkCreateClassSubjectsRequest,
  BulkCreateClassSubjectsResponse,
  CreateClassSubjectGroupRequest,
  CreateClassSubjectGroupResponse,
  CreateSubjectRequest,
  CreateSubjectResponse,
  ListBoardSubjectDefaultsResponse,
  ListClassSubjectGroupsResponse,
  ListSubjectsQuery,
  ListSubjectsResponse,
  SeedBoardSubjectDefaultsResponse,
  SoftDeleteSubjectRequest,
  SubjectSummary,
  UpdateSubjectRequest,
} from '../types';
import { appendPaginationQuery } from '../../../types/pagination';

export function bindSubjects(client: ApiClient) {
  return {
    async listSubjects(params?: ListSubjectsQuery): Promise<ApiResponse<ListSubjectsResponse>> {
      const q = new URLSearchParams();
      if (params?.class_id) q.set('class_id', params.class_id);
      if (params?.selection_type) q.set('selection_type', params.selection_type);
      appendPaginationQuery(q, params);
      const qs = q.toString();
      return client.request<ListSubjectsResponse>(`/subjects${qs ? `?${qs}` : ''}`);
    },

    /** Fetch every subject tenant-wide (follows pagination; default API page is only 50). */
    async listAllSubjects(params?: Omit<ListSubjectsQuery, 'page' | 'limit' | 'offset'>): Promise<SubjectSummary[]> {
      const pageSize = 200;
      const all: SubjectSummary[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const q = new URLSearchParams({
          page: String(page),
          limit: String(pageSize),
        });
        if (params?.class_id) q.set('class_id', params.class_id);
        if (params?.selection_type) q.set('selection_type', params.selection_type);
        const res = await client.request<ListSubjectsResponse>(`/subjects?${q}`);
        const batch = res.data?.subjects ?? [];
        all.push(...batch);
        totalPages = Math.max(1, Number(res.data?.total_pages ?? 1));
        page += 1;
      } while (page <= totalPages);
      return all;
    },

    /** Fetch every subject for a class (follows pagination up to the API max page size). */
    async listAllSubjectsForClass(classId: string): Promise<SubjectSummary[]> {
      const pageSize = 200;
      const all: SubjectSummary[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const q = new URLSearchParams({
          class_id: classId,
          page: String(page),
          limit: String(pageSize),
        });
        const res = await client.request<ListSubjectsResponse>(`/subjects?${q}`);
        const batch = res.data?.subjects ?? [];
        all.push(...batch);
        totalPages = Math.max(1, Number(res.data?.total_pages ?? 1));
        page += 1;
      } while (page <= totalPages);
      return all;
    },

    async listBoardSubjectDefaults(params?: {
      board_code?: string;
      grade_level?: number;
    }): Promise<ApiResponse<ListBoardSubjectDefaultsResponse>> {
      const q = new URLSearchParams();
      if (params?.board_code) q.set('board_code', params.board_code);
      if (params?.grade_level != null) q.set('grade_level', String(params.grade_level));
      const qs = q.toString();
      return client.request<ListBoardSubjectDefaultsResponse>(
        `/board-subject-defaults${qs ? `?${qs}` : ''}`,
      );
    },

    async seedBoardSubjectDefaults(): Promise<ApiResponse<SeedBoardSubjectDefaultsResponse>> {
      return client.request<SeedBoardSubjectDefaultsResponse>('/subjects/seed-board-defaults', {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    async createSubject(body: CreateSubjectRequest): Promise<ApiResponse<CreateSubjectResponse>> {
      return client.request<CreateSubjectResponse>('/subjects', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async updateSubject(body: UpdateSubjectRequest): Promise<ApiResponse<void>> {
      return client.request<void>('/subjects/update', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async softDeleteSubject(body: SoftDeleteSubjectRequest): Promise<ApiResponse<void>> {
      return client.request<void>('/subjects/delete', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async bulkCreateClassSubjects(
      body: BulkCreateClassSubjectsRequest
    ): Promise<ApiResponse<BulkCreateClassSubjectsResponse>> {
      return client.request<BulkCreateClassSubjectsResponse>('/classes/subjects/bulk', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async listClassSubjectGroups(
      classId: string
    ): Promise<ApiResponse<ListClassSubjectGroupsResponse>> {
      const q = new URLSearchParams({ class_id: classId });
      return client.request<ListClassSubjectGroupsResponse>(`/classes/subject-groups?${q}`);
    },

    async createClassSubjectGroup(
      body: CreateClassSubjectGroupRequest
    ): Promise<ApiResponse<CreateClassSubjectGroupResponse>> {
      return client.request<CreateClassSubjectGroupResponse>('/classes/subject-groups', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  };
}
